import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRightIcon, SparklesIcon, CheckCircle2Icon, GlobeIcon, ZapIcon, Rocket } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

export function PremiumCTA() {
  const { isDark } = useTheme();
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const badgeRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const buttonsRef = useRef(null);

  useEffect(() => {
    if (!cardRef.current) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: cardRef.current,
          start: 'top 85%',   
          toggleActions: 'play none none none',
          once: true 
        }
      });

      tl.fromTo(cardRef.current,
        { opacity: 0, y: 40, scale: 0.98 },
        { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'power3.out' }
      );

      tl.fromTo([badgeRef.current, titleRef.current, descRef.current],
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.4, stagger: 0.08 },
        "-=0.3"
      );

      if (buttonsRef.current) {
        tl.fromTo(buttonsRef.current.children,
          { opacity: 0, y: 15 },
          { opacity: 1, y: 0, duration: 0.35, stagger: 0.06 },
          "-=0.3"
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="premiumcta" ref={containerRef} className="relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        <div
          className="absolute top-1/2 left-1/4 w-96 h-96 rounded-full blur-2xl animate-pulse"
          style={{
            background: isDark
              ? 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(6, 182, 212, 0.1) 0%, transparent 70%)',
            animationDuration: '0.5s'
          }}
        />
        <div
          className="absolute top-1/2 right-1/4 w-96 h-96 rounded-full blur-2xl animate-pulse"
          style={{
            background: isDark
              ? 'radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)',
            animationDuration: '0.5s',
            animationDelay: '2s'
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Main Card Container */}
        <div
          ref={cardRef}
          className={`relative rounded-3xl lg:rounded-[3rem] overflow-hidden border-2 p-1 shadow-2xl backdrop-blur-sm ${
            isDark
              ? 'border-white/[0.08] bg-slate-900/60 shadow-cyan-500/10'
              : 'border-slate-200/80 bg-white/80 shadow-slate-900/5'
          }`}
        >
          {/* Inner Gradient Card */}
          <div
            className={`relative rounded-[2.5rem] lg:rounded-[2.8rem] overflow-hidden px-6 py-16 sm:px-12 sm:py-20 lg:px-20 lg:py-28 ${
              isDark
                ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950/20'
                : 'bg-gradient-to-br from-white via-slate-50 to-blue-50/30'
            }`}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
            <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />

            <div className="relative z-10 flex flex-col items-center text-center">
              {/* Top Badge */}
              <motion.div
                ref={badgeRef}
                // ⚡ OPTIMIZED: Only transform
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold tracking-wide uppercase mb-8 lg:mb-10 border shadow-lg cursor-pointer transition-transform duration-300 hover:scale-105 ${
                  isDark
                    ? 'bg-gradient-to-r from-cyan-500/15 to-blue-500/15 border-cyan-500/30 text-cyan-400 shadow-cyan-500/20'
                    : 'bg-gradient-to-r from-cyan-100 to-blue-100 border-cyan-300 text-cyan-700 shadow-cyan-500/10'
                }`}
              >
                <Rocket className="w-4 h-4" />
                Start in under 5 minutes
                <SparklesIcon className="w-4 h-4 animate-pulse" />
              </motion.div>

              {/* Main Heading */}
              <h2
                ref={titleRef}
                className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-6 lg:mb-8 leading-[1.1] ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}
              >
                Ready to transform your{' '}
                <br className="hidden sm:block" />
                <span
                  className={`relative inline-block ${
                    isDark
                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400'
                      : 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600'
                  }`}
                >
                  payment experience?
                  <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-indigo-500/20 blur-xl -z-10" />
                </span>
              </h2>

              {/* Description */}
              <p
                ref={descRef}
                className={`text-lg sm:text-xl lg:text-2xl max-w-3xl mx-auto mb-10 lg:mb-12 leading-relaxed ${
                  isDark ? 'text-slate-400' : 'text-slate-600'
                }`}
              >
                Join thousands of businesses already using 1401. Start
                accepting payments in minutes with our{' '}
                <span className={`font-bold ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  developer-friendly APIs
                </span>
                .
              </p>

              {/* Feature Pills */}
              <div className="flex flex-wrap justify-center gap-4 lg:gap-6 mb-10 lg:mb-14">
                {[
                  { icon: <CheckCircle2Icon className="w-4 h-4" />, text: 'No setup fees' },
                  { icon: <GlobeIcon className="w-4 h-4" />, text: 'Global coverage' },
                  { icon: <ZapIcon className="w-4 h-4" />, text: 'Instant activation' }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    // ⚡ OPTIMIZED: Only transform
                    className={`opacity-0 animate-fadeIn flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-transform duration-300 hover:scale-105 ${
                      isDark
                        ? 'bg-white/[0.05] text-slate-300 border border-white/[0.08] hover:bg-white/[0.08]'
                        : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
                    }`}
                  >
                    <span className={isDark ? 'text-cyan-400' : 'text-cyan-600'}>{item.icon}</span>
                    {item.text}
                  </motion.div>
                ))}
              </div>

              {/* Action Buttons */}
              <div
                ref={buttonsRef}
                className="flex flex-col sm:flex-row items-center justify-center gap-4 lg:gap-6 w-full sm:w-auto"
              >
                <a
                  href="panel.html#/login"
                  // ⚡ OPTIMIZED: Only transform
                  className={`group relative inline-flex items-center justify-center gap-3 px-8 lg:px-10 py-4 lg:py-5 rounded-xl lg:rounded-2xl text-base lg:text-lg font-bold shadow-xl transition-transform duration-300 hover:-translate-y-1 active:scale-95 w-full sm:w-auto overflow-hidden ${
                    isDark
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-500/40'
                      : 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-cyan-600/30 hover:shadow-2xl hover:shadow-cyan-600/40'
                  }`}
                >
                  <Rocket className="w-5 h-5 transition-transform duration-200 group-hover:rotate-12" />
                  Start Building Free
                  <ArrowRightIcon className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />

                  {/* Button Shine Effect */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  </div>
                </a>

                <a
                  href="#footer"
                  // ⚡ OPTIMIZED: Only transform
                  className={`group inline-flex items-center justify-center gap-3 px-8 lg:px-10 py-4 lg:py-5 rounded-xl lg:rounded-2xl text-base lg:text-lg font-bold border-2 transition-transform duration-300 hover:-translate-y-1 active:scale-95 w-full sm:w-auto ${
                    isDark
                      ? 'border-slate-700 text-white hover:bg-slate-800 hover:border-cyan-500/30'
                      : 'border-slate-300 text-slate-900 hover:bg-slate-50 hover:border-cyan-400'
                  }`}
                >
                  Talk to Sales
                  <ArrowRightIcon className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Decorative Line */}
        <div
          // ⚡ OPTIMIZED: Only opacity
          className={`mt-12 lg:mt-16 flex justify-center items-center gap-6 lg:gap-8 transition-opacity duration-500 hover:opacity-100 ${
            isDark ? 'opacity-40' : 'opacity-50'
          }`}
        >
          <div className={`h-px flex-grow ${isDark ? 'bg-slate-800' : 'bg-slate-300'}`} />
          <span
            className={`text-xs lg:text-sm font-bold uppercase tracking-widest ${
              isDark ? 'text-slate-600' : 'text-slate-500'
            }`}
          >
            Enterprise Ready
          </span>
          <div className={`h-px flex-grow ${isDark ? 'bg-slate-800' : 'bg-slate-300'}`} />
        </div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 1 }}
          className={`mt-12 flex flex-wrap justify-center items-center gap-6 lg:gap-8 text-sm font-semibold ${
            isDark ? 'text-slate-500' : 'text-slate-600'
          }`}
        >
          <div className="flex items-center gap-2">
            <CheckCircle2Icon className={`w-4 h-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span>PCI DSS Certified</span>
          </div>
          <div className={`w-px h-4 ${isDark ? 'bg-slate-800' : 'bg-slate-300'}`} />
          <div className="flex items-center gap-2">
            <CheckCircle2Icon className={`w-4 h-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span>SOC 2 Compliant</span>
          </div>
          <div className={`w-px h-4 ${isDark ? 'bg-slate-800' : 'bg-slate-300'}`} />
          <div className="flex items-center gap-2">
            <CheckCircle2Icon className={`w-4 h-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span>99.99% Uptime SLA</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}